import { useEffect, useRef, useState } from 'react'
import { useQuery } from 'react-query'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { queryClient } from '../../../config/queryClient'
import { QueryKeys } from '../../../enums/queryKeys'
import { fetchBankAccounts } from '../query-api'
import { BankAccount } from '../types/bankAccount'

const POLLING_INTERVAL_MS = 1000
const MAX_POLLING_DURATION_MS = 30000

interface UsePollBankAccountByBookkeepingIdProps {
  polling?: boolean
  bookkeepingAccountId: string | null
  pollingInterval?: number
  maxPollingDuration?: number
  onSuccess: (bankAccount: BankAccount) => void
  onTimeout: () => void
}

export const usePollBankAccountByBookkeepingId = ({
  polling = false,
  bookkeepingAccountId,
  pollingInterval = POLLING_INTERVAL_MS,
  maxPollingDuration = MAX_POLLING_DURATION_MS,
  onSuccess,
  onTimeout,
}: UsePollBankAccountByBookkeepingIdProps) => {
  const { organization } = useUserOrganization()
  const [isActive, setIsActive] = useState(false)
  const doneRef = useRef(false)
  const callbacksRef = useRef({ onSuccess, onTimeout })

  callbacksRef.current = { onSuccess, onTimeout }

  const { data } = useQuery(
    [QueryKeys.BankAccountsPolling, bookkeepingAccountId],
    () => fetchBankAccounts(organization?.id ?? ''),
    {
      enabled: !!organization?.id && isActive,
      refetchInterval: isActive ? pollingInterval : false,
      refetchIntervalInBackground: false,
    },
  )

  // Start polling and set up timeout
  useEffect(() => {
    if (!polling) {
      return
    }

    doneRef.current = false
    setIsActive(true)

    const timer = window.setTimeout(() => {
      if (doneRef.current) {
        return
      }
      doneRef.current = true
      setIsActive(false)
      callbacksRef.current.onTimeout()
    }, maxPollingDuration)

    return () => {
      window.clearTimeout(timer)
      setIsActive(false)
    }
  }, [polling, maxPollingDuration])

  // Detect when the bank account appears
  useEffect(() => {
    if (!isActive || !bookkeepingAccountId || doneRef.current) {
      return
    }

    const match = data?.data.find((ba) => ba.bookkeepingAccountId === bookkeepingAccountId)

    if (match) {
      doneRef.current = true
      setIsActive(false)
      queryClient.invalidateQueries(QueryKeys.BankAccounts)
      callbacksRef.current.onSuccess(match)
    }
  }, [data, isActive, bookkeepingAccountId])
}
